import * as React from 'react'
// libs
import moment from 'moment'
import type { Moment } from 'moment'
// constants
import { ENTITIES } from 'constants/entities'
import { TASKS_TYPES } from 'constants/home'
import { REVIEW_STATUSES } from 'constants/review'
// types
import { IPlan, IPlanAction, IPlanItem, IPlanKpi, IPlanObjective, IReview } from 'types/graphql'
// utils
import { get, isEmpty } from 'lodash'
import { generateReviews } from 'utils/reviews'
import { map, pipe, filter, flatten } from 'lodash/fp'

const generateTasksFromReviews = ({
  id,
  path,
  type,
  reviews,
  entityType,
  description
}: (IPlanObjective | IPlanAction | IPlanKpi) & {
  path: string
  type: string
  entityType: string
}) => {
  return (reviews as (IReview & { dueDate: string })[]).map(review => ({
    id,
    type,
    path,
    entityType,
    description,
    review: review,
    isOpen: get(review, 'status', REVIEW_STATUSES.NOT_STARTED) !== REVIEW_STATUSES.COMPLETE,
    dueDate: review.dueDate,
    isOverdue: isTaskOverdue(review.dueDate),
    assignedPersons: review.scheduledReview.scheduledReviewers.map(reviewer => reviewer.user || reviewer.position)
  }))
}

export const isTaskOverdue = (dueDate: string | Moment) => moment(dueDate).isBefore(moment(), 'day')

export const usePlanItemsReviews = (planItems: IPlanItem[], plan: IPlan) => {
  const planReviews = React.useMemo(() => {
    return pipe(
      ({ id, name }) => ({
        id,
        description: name,
        entityType: ENTITIES.PLAN,
        type: TASKS_TYPES.REVIEW,
        reviews: generateReviews({ entity: plan })
      }),
      generateTasksFromReviews
    )(plan)
  }, [plan])

  const generateTaskData = React.useCallback(() => {
    const generateTasks = (planItems: IPlanItem[], entityKey: string, entityType: string) =>
      pipe(
        filter((planItem: IPlanItem) => !!planItem[entityKey]),
        map<any, any>(({ [entityKey]: entity, path }) => ({
          path,
          entityType,
          id: entity.id,
          type: TASKS_TYPES.REVIEW,
          reviews: generateReviews({ entity }),
          description: entity.description
        })),
        filter(entity => !isEmpty(entity.reviews)),
        map(entity => generateTasksFromReviews(entity)),
        flatten
      )(planItems)

    return {
      planKPIReviews: generateTasks(planItems, 'planKPI', ENTITIES.PLAN_KPI),
      planActionReviews: generateTasks(planItems, 'planAction', ENTITIES.PLAN_ACTION),
      planObjectiveReviews: generateTasks(planItems, 'planObjective', ENTITIES.PLAN_OBJECTIVE)
    }
  }, [planItems])

  const { planKPIReviews, planActionReviews, planObjectiveReviews } = generateTaskData()

  return {
    planReviews,
    planKPIReviews,
    planActionReviews,
    planObjectiveReviews
  }
}
